/**
 * LanguageSwitcher — toggle between English and Spanish.
 *
 * Renders as two buttons (EN | ES). The active language is visually highlighted.
 * Choice is persisted to localStorage via the i18n module's setLanguage.
 */

import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { setLanguage, getLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/lib/i18n'

const LABELS: Record<SupportedLanguage, string> = {
  en: 'EN',
  es: 'ES',
}

export function LanguageSwitcher(): ReactNode {
  const { t } = useTranslation()
  const current = getLanguage()

  return (
    <div
      role="group"
      aria-label={t('settings.language')}
      style={{
        display:      'inline-flex',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-md)',
        overflow:     'hidden',
      }}
    >
      {SUPPORTED_LANGUAGES.map((lang) => (
        <button
          key={lang}
          type="button"
          aria-pressed={lang === current}
          onClick={() => setLanguage(lang)}
          style={{
            padding:    '0.3rem 0.6rem',
            fontSize:   '0.75rem',
            fontWeight: lang === current ? 700 : 400,
            background: lang === current ? 'var(--brand-primary)' : 'transparent',
            color:      lang === current ? 'var(--brand-white)' : 'var(--brand-neutral-700)',
            border:     'none',
            cursor:     'pointer',
          }}
        >
          {LABELS[lang]}
        </button>
      ))}
    </div>
  )
}
